/**
 * Paginas dobles partidas en dos: que parejas van juntas en cada capitulo.
 *
 * Desde el capitulo 381 de Black Clover las dobles no llegan enteras: la
 * fuente pasa a ser el rip de MANGA Plus, que las publica **cortadas por la
 * mitad** en dos ficheros del tamano de una pagina normal. Medido en el
 * servidor: hasta el 380 la pagina normal es de 1200x1800 y la doble llega
 * entera a 2133x1600; del 381 en adelante todo son ficheros de 784x1145.
 *
 * Reconocerlas lo hace `lib/costuras`, mirando si el dibujo continua en el
 * corte. Aqui solo se guarda el resultado y lo que diga el usuario, que manda
 * siempre: lo que une o separa a mano no lo vuelve a tocar el automatico.
 *
 * Se guarda por capitulo y **solo en este aparato**: Suwayomi no tiene donde
 * apuntar algo asi.
 */

import type { Orden } from './costuras'

export type { Orden }

const CLAVE = 'yomou.dobles.'

/**
 * Version del reconocedor de dobles.
 *
 * Al subirla se tira **todo** lo guardado por las versiones anteriores, hasta
 * lo que el usuario toco a mano: si las dobles se pintaban del reves o el
 * reconocedor se equivocaba, lo que se toco entonces se toco contra un lector
 * roto, y arrastrarlo para siempre es peor que volver a mirar. Es lo mismo que
 * hace `formato.ts` con la deteccion de manhwa.
 *
 *   1  1.4.0, unir a mano y del reves
 *   2  1.4.1 y 1.4.2, automatico con el sentido de lectura
 *   3  la colocacion la decide la medida, no el sentido
 *   4  1.6.3, el corte se mide con el parecido y el escalon
 */
const VERSION = 4

export interface Dobles {
  /**
   * Las dobles, por el indice en el que empiezan y con su colocacion: con el
   * 16 dentro, la 16 y la 17 van juntas.
   */
  readonly unidas: ReadonlyMap<number, Orden>
  /** Las que el usuario separo a mano. El automatico ya no las toca. */
  readonly sueltas: ReadonlySet<number>
}

export const SIN_DOBLES: Dobles = { unidas: new Map(), sueltas: new Set() }

interface Guardado {
  v?: number
  u?: [number, Orden][]
  s?: number[]
}

function enteros(lista: unknown): number[] {
  if (!Array.isArray(lista)) return []
  return lista.filter((n): n is number => Number.isInteger(n) && n >= 0)
}

function parejas(lista: unknown): [number, Orden][] {
  if (!Array.isArray(lista)) return []
  return lista.filter(
    (p): p is [number, Orden] =>
      Array.isArray(p) && Number.isInteger(p[0]) && p[0] >= 0 && (p[1] === 'rtl' || p[1] === 'ltr'),
  )
}

export function leerDobles(capituloId: number): Dobles {
  try {
    const crudo = localStorage.getItem(CLAVE + capituloId)
    if (!crudo) return SIN_DOBLES
    const datos = JSON.parse(crudo) as Guardado
    if (!datos || datos.v !== VERSION) return SIN_DOBLES
    return { unidas: new Map(parejas(datos.u)), sueltas: new Set(enteros(datos.s)) }
  } catch {
    return SIN_DOBLES
  }
}

function guardar(capituloId: number, dobles: Dobles): Dobles {
  try {
    if (dobles.unidas.size === 0 && dobles.sueltas.size === 0) {
      localStorage.removeItem(CLAVE + capituloId)
    } else {
      localStorage.setItem(
        CLAVE + capituloId,
        JSON.stringify({
          v: VERSION,
          u: [...dobles.unidas].sort((a, b) => a[0] - b[0]),
          s: [...dobles.sueltas].sort((a, b) => a - b),
        }),
      )
    }
  } catch {
    // Sin sitio en el almacen se sigue leyendo igual, solo que sin recordarlo
  }
  return dobles
}

/**
 * Une la pagina con la siguiente, o las separa si ya estaban juntas.
 *
 * Las parejas no se pueden solapar: si la de al lado ya estaba unida a esta,
 * se deshace antes, o una pagina saldria en dos sitios a la vez.
 */
export function alternarDoble(
  capituloId: number,
  indice: number,
  dobles: Dobles,
  orden: Orden,
): Dobles {
  const unidas = new Map(dobles.unidas)
  const sueltas = new Set(dobles.sueltas)
  if (unidas.has(indice)) {
    unidas.delete(indice)
    // Queda apuntado para que el automatico no vuelva a unirlas
    sueltas.add(indice)
  } else {
    unidas.delete(indice - 1)
    unidas.delete(indice + 1)
    unidas.set(indice, orden)
    sueltas.delete(indice)
  }
  return guardar(capituloId, { unidas, sueltas })
}

/**
 * Apunta una pareja que ha reconocido el automatico.
 *
 * Devuelve las mismas `dobles` si no hay nada que cambiar, para que el lector
 * no vuelva a pintarse por gusto.
 */
export function apuntarAutomatica(
  capituloId: number,
  indice: number,
  dobles: Dobles,
  orden: Orden,
): Dobles {
  if (dobles.unidas.has(indice) || dobles.sueltas.has(indice)) return dobles
  if (dobles.unidas.has(indice - 1) || dobles.unidas.has(indice + 1)) return dobles
  const unidas = new Map(dobles.unidas)
  unidas.set(indice, orden)
  return guardar(capituloId, { unidas, sueltas: dobles.sueltas })
}

/** ¿Esta pagina y la siguiente se pintan juntas? */
export function esDoble(indice: number, dobles: Dobles): boolean {
  return dobles.unidas.has(indice)
}

/** Como va colocada la doble que empieza aqui. */
export function ordenDe(indice: number, dobles: Dobles): Orden {
  return dobles.unidas.get(indice) ?? 'rtl'
}

/**
 * La pagina por la que empieza la unidad de lectura en la que cae `indice`.
 *
 * Hace falta porque el indice tambien llega de fuera —la barra de abajo, el
 * progreso guardado, el capitulo que se retoma— y puede caer en la segunda
 * mitad de una doble, que no se pinta sola.
 */
export function inicioUnidad(indice: number, dobles: Dobles): number {
  return dobles.unidas.has(indice - 1) ? indice - 1 : indice
}

/** La siguiente unidad de lectura: una doble se pasa de una vez. */
export function siguienteUnidad(indice: number, dobles: Dobles): number {
  return dobles.unidas.has(indice) ? indice + 2 : indice + 1
}

/** La unidad anterior, cayendo en su primera mitad si era doble. */
export function anteriorUnidad(indice: number, dobles: Dobles): number {
  return inicioUnidad(indice - 1, dobles)
}
